import React, { useState, useEffect } from 'react';
import { getLastResults, getDetailResults } from '@/services/casinoApi';
import ResultModalLayout from '@/components/ResultModalLayout';
import "./Result.css";

// Helper function to map card string to image path
const getCardImage = (cardCode) => {
    if (!cardCode) return null;

    try {
        if (cardCode.length < 3) {
            return null;
        }

        // Use card-results folder with full card code (e.g., "8CC.jpg", "JDD.jpg")
        // Format: rank + suit (DD, SS, CC, HH)
        // NOTE: We need to ensure these assets are available in public assets
        return `/assets/img/card-results/${cardCode}.jpg`;
    } catch (e) {
        return null;
    }
};

// Helper function to parse card string and get array of card images
const parseCards = (cardString) => {
    if (!cardString) return [];

    const cards = cardString.split(',').map(card => card.trim());
    return cards.map(card => getCardImage(card)).filter(card => card !== null);
};

// Helper function to parse rdesc string
// Format: "No (13)#Big  Small  Small  Small  Small#No  Yes  No  Yes  No#Yes"
const parseRdesc = (rdesc) => {
    if (!rdesc) return {
        raceTo17: "",
        bigCard: [],
        zeroCard: [],
        oneZeroCard: ""
    };

    const parts = rdesc.split('#').map(part => part.trim());

    return {
        raceTo17: parts[0] || "", // "No (13)"
        bigCard: parts[1] ? parts[1].split(/\s+/).filter(s => s.length > 0) : [], // ["Big", "Small", "Small", "Small", "Small"]
        zeroCard: parts[2] ? parts[2].split(/\s+/).filter(s => s.length > 0) : [], // ["No", "Yes", "No", "Yes", "No"]
        oneZeroCard: parts[3] || "" // "Yes"
    };
};

// Helper function to determine if result is Yes or No for last results display
const getYesNoFromWin = (winValue, winnat) => {
    if (winnat) {
        const winnatLower = winnat.toLowerCase();
        if (winnatLower.includes('yes')) return 'Y';
        if (winnatLower.includes('no')) return 'N';
    }

    // win "1" = Yes, "0" = No
    const win = winValue?.toString() || "0";
    return win === "1" ? "Y" : "N";
};

const ResultRace17 = ({
    data = [],
    gameData = {},
}) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedResult, setSelectedResult] = useState(null);
    const [lastResults, setLastResults] = useState([]);
    const [detailResult, setDetailResult] = useState(null);
    const [loading, setLoading] = useState(false);

    // Fetch last results on mount
    useEffect(() => {
        const fetchLastResults = async () => {
            try {
                const response = await getLastResults('race17');
                if (response?.success && response?.data?.data?.res) {
                    setLastResults(response.data.data.res);
                }
            } catch (error) {
                console.error('Error fetching last results:', error);
            }
        };

        fetchLastResults();
        const interval = setInterval(fetchLastResults, 5000);
        return () => clearInterval(interval);
    }, []);

    const handleResultClick = async (resultItem, index) => {
        setSelectedResult(resultItem);
        setLoading(true);

        const mid = resultItem?.mid;

        if (mid) {
            try {
                const response = await getDetailResults('race17', mid.toString());
                if (response?.success && response?.data?.data?.t1) {
                    setDetailResult(response.data.data.t1);
                }
            } catch (error) {
                console.error('Error fetching detail results:', error);
            }
        }

        setLoading(false);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedResult(null);
        setDetailResult(null);
    };

    // Format modal data based on selected result
    const getModalData = () => {
        if (!selectedResult || !detailResult) return null;

        const cards = parseCards(detailResult.card);
        const rdescData = parseRdesc(detailResult.rdesc);

        return {
            roundId: detailResult.rid || gameData?.data?.data?.mid || gameData?.mid || "N/A",
            matchTime: detailResult.mtime || gameData?.data?.data?.mt || new Date().toLocaleString(),
            cards: cards,
            rdescData: rdescData,
            winnat: detailResult.winnat || "",
        };
    };

    const modalData = getModalData();

     // Parse cards from detailResult
     const cards = detailResult?.card ? parseCards(detailResult.card) : [];
     const rdescData = detailResult?.rdesc ? parseRdesc(detailResult.rdesc) : {};

    // Combine API results with fallback data
    const allResults = lastResults.length > 0
        ? lastResults
        : data.map((res, idx) => ({ win: res === "Y" ? "1" : "0", mid: null, winnat: res === "Y" ? "Yes" : "No" }));

    return (
        <>
            <div className="result-row">
                {allResults.map((resultItem, index) => {
                    const yesNo = getYesNoFromWin(resultItem.win, resultItem.winnat);
                    const isYes = yesNo === "Y";

                    return (
                        <div
                            key={index}
                            className={`result-circle ${isYes ? 'result-yes' : 'result-no'}`}
                            onClick={() => resultItem.mid && handleResultClick(resultItem, index)}
                            style={{ cursor: resultItem.mid ? 'pointer' : 'default' }}
                        >
                            {yesNo}
                        </div>
                    );
                })}
            </div>

            {/* Modal */}
            {modalData && (
                <ResultModalLayout
                    show={showModal}
                    onHide={handleCloseModal}
                    title="Race to 17 Result"
                    roundId={modalData.roundId}
                    matchTime={modalData.matchTime}
                    loading={loading}
                >
                  <div>
            <div className="row">
                <div className="col-12">
                    <h6 className="text-right">
                        <b>Round Id :</b> {modalData.roundId}
                    </h6>
                    <div className="text-center luckyseven-img">
                        <div className="row">
                            {cards.map((card, index) => (
                                card && (
                                    <div key={index} className="col">
                                        <img
                                            src={card}
                                            alt={`Card ${index + 1}`}
                                            onError={(e) => {
                                                // Fallback to local assets
                                                const cardName = card.split('/').pop();
                                                const fallbackPath = `/assets/img/card-results/${cardName}`;
                                                if (e.target.src !== fallbackPath) {
                                                    e.target.src = fallbackPath;
                                                } else {
                                                    e.target.style.display = 'none';
                                                }
                                            }}
                                        />
                                    </div>
                                )
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <p className="text-left mt-3">
                {rdescData.raceTo17 && (
                    <>
                        <b className="text-success">Race to 17: </b>
                        {rdescData.raceTo17}
                        <br />
                    </>
                )}
                {rdescData.bigCard.length > 0 && (
                    <>
                        <b className="text-success">Big Card: </b>
                        {rdescData.bigCard.join('  ')}
                        <br />
                    </>
                )}
                {rdescData.zeroCard.length > 0 && (
                    <>
                        <b className="text-success">Zero Card: </b>
                        {rdescData.zeroCard.join('  ')}
                        <br />
                    </>
                )}
                {rdescData.oneZeroCard && (
                    <>
                        <b className="text-success">One Zero Card: </b>
                        {rdescData.oneZeroCard}
                        <br />
                    </>
                )}
            </p>
        </div>
                </ResultModalLayout>
            )}

            <style>{`
        @keyframes cardRotate {
          from {
            transform: rotateY(0deg);
          }
          to {
            transform: rotateY(360deg);
          }
        }
        .card-wrapper {
          display: inline-block;
        }
        .card-image {
          width: auto;
          height: 120px;
          object-fit: contain;
          border-radius: 4px;
          border: 2px solid #ffd700;
          transform: none !important;
        }

        /* Override any l-rotate class if applied */
        .card-image.l-rotate,
        .l-rotate .card-image {
          transform: none !important;
        }

        /* Race17 specific result colors */
        .result-circle.result-yes {
          background-color: #355E3B;
          color: #ffeb3b;
        }

        .result-circle.result-no {
          background-color: #355E3B;
          color: #ff9800;
        }
      `}</style>
        </>
    );
};

export default ResultRace17;
